.fieldset {
  display: flex;
  gap: var(--tuna-spacing-1);
  border: none;
  margin: 0;
  padding: 0;
  min-inline-size: 0;
}

/*
 * The date is already visible in the row, so the legend exists only for the accessible name.
 * Visually hidden rather than `display: none`, which would remove it from the accessibility
 * tree along with the grouping it provides.
 */
.legend {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.yes,
.maybe,
.no {
  display: inline-flex;
  align-items: center;
  /* Containing block for the absolutely-positioned input below. */
  position: relative;
  gap: var(--tuna-spacing-1);
  min-height: 44px;
  padding: 0 var(--tuna-spacing-2);
  border: 1px solid var(--tuna-border-default);
  border-radius: var(--tuna-shape-borderRadius);
  color: var(--tuna-content-secondary);
  font-size: var(--tuna-typography-body2-fontSize);
  cursor: pointer;
}

.yes:has(.input:checked) {
  border-color: var(--tuna-feedback-success);
  color: var(--tuna-feedback-success);
}

.maybe:has(.input:checked) {
  border-color: var(--tuna-feedback-warning);
  color: var(--tuna-feedback-warning-text);
}

.no:has(.input:checked) {
  border-color: var(--tuna-feedback-error);
  color: var(--tuna-feedback-error);
}

.fieldset:disabled .yes,
.fieldset:disabled .maybe,
.fieldset:disabled .no {
  color: var(--tuna-palette-text-disabled);
  border-color: var(--tuna-border-subtle);
  cursor: default;
}

/* The input now covers the pill, so it owns the cursor and must follow it into disabled. */
.fieldset:disabled .input {
  cursor: default;
}

.glyph {
  display: inline-flex;
  align-items: center;
}

/*
 * The pill and its glyph ARE the indicator, so the native radio must paint nothing. It stays
 * in the DOM, focusable and exposed as a radio — only its appearance is suppressed. Without
 * `appearance: none` the browser drew its own dot beside the glyph, and in dark mode the
 * unselected dot read as filled while the selected one read as blue, inverting the meaning.
 *
 * Hidden by transparency, never by collapsing the box. This mirrors ui-lib's own radio
 * (`frontend/ui-lib/src/primitives/radio.module.css:11`): absolutely positioned, full size,
 * transparent. A zero-size input has an EMPTY bounding box, which is treated as hidden —
 * it took out every rehearsal-poll E2E spec at setup — and it is a poor focus and hit target
 * besides. At full size the input IS the pill's hit area, so a click anywhere on the pill
 * activates the radio natively rather than relying on label association.
 */
.input {
  appearance: none;
  -webkit-appearance: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  cursor: pointer;
}

/* The ring lands on the pill, since the input itself no longer paints anything. */
.yes:has(.input:focus-visible),
.maybe:has(.input:focus-visible),
.no:has(.input:focus-visible) {
  outline: 2px solid var(--tuna-palette-primary-main);
  outline-offset: 2px;
}

/* The single-column list is the mobile layout; only the control itself goes full-width. */
@media (max-width: 599.95px) {
  .fieldset {
    width: 100%;
  }

  .yes,
  .maybe,
  .no {
    flex: 1;
    justify-content: center;
  }
}
